// Bet grading — ONE copy of the rules, used by the site (src/App.js) AND by the server (server/settle.js).
// Written in CommonJS on purpose so Node can require it as is; the site imports it the usual way.
// evaluateBet(bet, gameDetails) -> 'won' | 'lost' | 'void' (stake back) | null (not decided yet)
// gameDetails = what the server's own detail routes return (/api/game-details/:id, /api/mlb-game-details/:id, …).
/* eslint-disable no-case-declarations */

const normalizeName = (name) => {
  if (!name) return '';
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')  // Remove combining diacritical marks
    .replace(/ø/g, 'o')   // Danish/Norwegian
    .replace(/Ø/g, 'O')
    .replace(/ł/g, 'l')   // Polish
    .replace(/Ł/g, 'L')
    .replace(/đ/g, 'd')   // Croatian
    .replace(/Đ/g, 'D')
    .replace(/ß/g, 'ss')  // German sharp S
    .replace(/æ/g, 'ae')  // Danish/Norwegian
    .replace(/Æ/g, 'AE')
    .replace(/œ/g, 'oe')  // French
    .replace(/Œ/g, 'OE')
    .toLowerCase()
    .trim();
};

// Evaluate a single bet against game data
// NHL id from a roster id ("ana-8474590" -> "8474590")
const nhlIdOfPlayer = (id) => String(id ?? '').split('-').pop();
// same player? exact name, or same last name + same first initial (never last name alone)
const samePlayerName = (a, b) => {
  const x = normalizeName(a), y = normalizeName(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const xp = x.split(' '), yp = y.split(' ');
  return xp[xp.length - 1] === yp[yp.length - 1] && (xp[0] || '')[0] === (yp[0] || '')[0];
};

// 'won' | 'lost' | 'void' (stake back: game postponed, player didn't play) | null (not decided yet)
const evaluateBet = (bet, gameDetails) => {
  const details = bet.bet_value ? JSON.parse(bet.bet_value) : {};
  // NFL: player stats + scoring plays from the box score (a player who didn't play = void)
  if (String(bet.bet_type).startsWith('nfl_')) {
    const ps = gameDetails?.playerStats?.[String(details.playerId)];
    switch (bet.bet_type) {
      case 'nfl_half_winner': {
        if (!gameDetails?.halfDone) return null;
        const h = gameDetails.homeHalf, a = gameDetails.awayHalf;
        const win = h > a ? 'home' : a > h ? 'away' : 'tie';
        return bet.selection === win ? 'won' : 'lost';
      }
      case 'nfl_both_20':
        if (!gameDetails?.isFinished) return null;
        return ((gameDetails.homeScore >= 20 && gameDetails.awayScore >= 20) === (bet.selection === 'yes')) ? 'won' : 'lost';
      case 'nfl_overtime':
        if (!gameDetails?.isFinished) return null;
        return gameDetails.overtime ? 'won' : 'lost';
      case 'nfl_anytime_td':
        if (!gameDetails?.isFinished) return null;
        if (!ps) return 'void';
        return (ps.rushTd + ps.recTd) > 0 ? 'won' : 'lost';
      case 'nfl_first_td': {
        if (!gameDetails?.isFinished) return null;
        if (!ps) return 'void';
        if (!gameDetails.firstTdText) return 'lost';
        const scorer = gameDetails.firstTdText.split(/ \d/)[0];
        return samePlayerName(scorer, details.playerName) ? 'won' : 'lost';
      }
      case 'nfl_pass_yds':
      case 'nfl_rush_yds':
      case 'nfl_rec_yds': {
        if (!gameDetails?.isFinished) return null;
        if (!ps) return 'void';
        const v = bet.bet_type === 'nfl_pass_yds' ? ps.passYds : bet.bet_type === 'nfl_rush_yds' ? ps.rushYds : ps.recYds;
        const l = parseFloat(details.line);
        return bet.selection === 'over' ? (v > l ? 'won' : 'lost') : (v < l ? 'won' : 'lost');
      }
      default: return null;
    }
  }
  // NFL moneyline: a tie refunds
  if (bet.bet_type === 'winner' && String(bet.game_id ?? bet.gameId ?? '').startsWith('nfl-') && gameDetails?.isFinished
      && gameDetails.homeScore === gameDetails.awayScore) return 'void';
  // F1: from the official classification (a driver who didn't start = void)
  if (String(bet.bet_type).startsWith('f1_')) {
    if (!gameDetails?.isFinished) return null;
    const res = gameDetails.results || [];
    const pos = (code) => res.find((r) => r.code === code)?.position;
    const mine = pos(bet.selection);
    switch (bet.bet_type) {
      case 'f1_winner': return mine == null ? 'void' : mine === 1 ? 'won' : 'lost';
      case 'f1_top3': return mine == null ? 'void' : mine <= 3 ? 'won' : 'lost';
      case 'f1_top6': return mine == null ? 'void' : mine <= 6 ? 'won' : 'lost';
      case 'f1_top10': return mine == null ? 'void' : mine <= 10 ? 'won' : 'lost';
      case 'f1_h2h': { const other = pos(details.vs); return mine == null || other == null ? 'void' : mine < other ? 'won' : 'lost'; }
      case 'f1_pole': return !gameDetails.pole ? 'void' : gameDetails.pole === bet.selection ? 'won' : 'lost';
      case 'f1_fastest_lap': return mine == null || !gameDetails.fastestLap ? 'void' : gameDetails.fastestLap === bet.selection ? 'won' : 'lost';
      case 'f1_constructor': return !gameDetails.winnerTeam ? 'void' : gameDetails.winnerTeam === bet.selection ? 'won' : 'lost';
      default: return null;
    }
  }
  const { homeTeam, awayTeam, totalGoals, firstGoal, bothTeamsScored, isFinished, goals } = gameDetails;
  if (gameDetails.isVoid) return 'void';   // postponed / cancelled

  switch (bet.bet_type) {
    case 'winner': {
      if (!isFinished) return null;
      const hS = gameDetails.homeScore ?? homeTeam?.score ?? 0;
      const aS = gameDetails.awayScore ?? awayTeam?.score ?? 0;
      if (bet.selection === 'home') return hS > aS ? 'won' : 'lost';
      return aS > hS ? 'won' : 'lost';
    }

    case 'total':
      if (!isFinished) return null;
      const line = parseFloat(details.line);
      const totalVal = totalGoals ?? gameDetails.totalPoints ?? ((gameDetails.homeScore ?? 0) + (gameDetails.awayScore ?? 0));
      // Use bet.selection which is always 'over'/'under' regardless of language
      if (bet.selection === 'over') {
        return totalVal > line ? 'won' : 'lost';
      } else {
        return totalVal < line ? 'won' : 'lost';
      }

    case 'first_goal':
      if (!firstGoal) return isFinished ? 'lost' : null;
      const playerName = normalizeName(details.playerName);
      const scorerName = normalizeName(firstGoal.scorerName);
      const playerNameParts = playerName?.split(' ') || [];
      const playerFirstName = playerNameParts[0] || '';
      const playerLastName = playerNameParts[playerNameParts.length - 1] || '';
      const scorerNameParts = scorerName?.split(' ') || [];
      const scorerFirstName = scorerNameParts[0] || '';
      const scorerLastName = scorerNameParts[scorerNameParts.length - 1] || '';
      // Exact full name match OR last name match with same first initial
      const match = scorerName === playerName ||
                    (playerLastName === scorerLastName && playerFirstName[0] === scorerFirstName[0]);
      return match ? 'won' : 'lost';

    case 'anytime_goal':
      if (!gameDetails.goalScorers || gameDetails.goalScorers.length === 0) {
        if (isFinished) return 'lost';
        return null;
      }
      const anytimePlayerName = normalizeName(details.playerName);
      const anytimeNameParts = anytimePlayerName?.split(' ') || [];
      const anytimeFirstName = anytimeNameParts[0] || '';
      const anytimeLastName = anytimeNameParts[anytimeNameParts.length - 1] || '';
      const scoredGoal = gameDetails.goalScorers.some(scorer => {
        const normalizedScorer = normalizeName(scorer);
        const scorerParts = normalizedScorer?.split(' ') || [];
        const sFirstName = scorerParts[0] || '';
        const sLastName = scorerParts[scorerParts.length - 1] || '';
        // Exact full name match OR last name match with same first initial
        if (normalizedScorer === anytimePlayerName) return true;
        if (anytimeLastName === sLastName && anytimeFirstName[0] === sFirstName[0]) return true;
        return false;
      });
      if (scoredGoal) return 'won';
      if (isFinished) return 'lost';
      return null;

    case 'player_points':
      if (!gameDetails.playersWithPoints || gameDetails.playersWithPoints.length === 0) {
        // No one has points yet - if game is finished, bet is lost
        if (isFinished) return 'lost';
        return null;
      }
      const pointsPlayerName = normalizeName(details.playerName);
      const pointsNameParts = pointsPlayerName?.split(' ') || [];
      const pointsFirstName = pointsNameParts[0] || '';
      const pointsLastName = pointsNameParts[pointsNameParts.length - 1] || '';
      const hasPoint = gameDetails.playersWithPoints.some(player => {
        const normalizedPlayer = normalizeName(player);
        const playerParts = normalizedPlayer?.split(' ') || [];
        const playerFirstName = playerParts[0] || '';
        const playerLastName = playerParts[playerParts.length - 1] || '';
        // Exact full name match
        if (normalizedPlayer === pointsPlayerName) return true;
        // Last name must match exactly, and first name must start the same (initial match)
        if (playerLastName === pointsLastName && playerFirstName[0] === pointsFirstName[0]) return true;
        return false;
      });
      if (hasPoint) return 'won';
      if (isFinished) return 'lost';
      return null;

    case 'winning_goal':
      if (!isFinished) return null;
      if (!gameDetails.goals || gameDetails.goals.length === 0) return 'lost';
      const losingTeamScore = Math.min(homeTeam.score, awayTeam.score);
      let winningGoalScorer = null;
      let winningGoalScorerId = null;
      let winningTeamGoalCount = 0;
      const winningTeamAbbrev = homeTeam.score > awayTeam.score ? homeTeam.abbrev : awayTeam.abbrev;
      for (const goal of gameDetails.goals) {
        if (goal.teamAbbrev === winningTeamAbbrev) {
          winningTeamGoalCount++;
          if (winningTeamGoalCount === losingTeamScore + 1) {
            winningGoalScorer = goal.scorerName;
            winningGoalScorerId = goal.scorerId != null ? String(goal.scorerId) : null;
            break;
          }
        }
      }
      if (!winningGoalScorer) return 'lost';   // e.g. decided in a shootout: no skater gets it
      const isWinningGoalScorer = (winningGoalScorerId && details.playerId)
        ? winningGoalScorerId === nhlIdOfPlayer(details.playerId)
        : samePlayerName(winningGoalScorer, details.playerName);
      return isWinningGoalScorer ? 'won' : 'lost';

    case 'team_first':
      if (!firstGoal) return isFinished ? 'lost' : null;
      if (bet.selection === 'home') {
        return firstGoal.teamAbbrev === homeTeam.abbrev ? 'won' : 'lost';
      } else {
        return firstGoal.teamAbbrev === awayTeam.abbrev ? 'won' : 'lost';
      }

    case 'both_score':
      if (bet.selection === 'yes') {
        if (bothTeamsScored) return 'won';
        if (isFinished) return 'lost';
        return null;
      } else {
        if (!isFinished) return null;
        return !bothTeamsScored ? 'won' : 'lost';
      }

    case 'exact_score': {
      if (!gameDetails.isFinished) return null;
      const hScore = gameDetails.homeScore ?? homeTeam?.score;
      const aScore = gameDetails.awayScore ?? awayTeam?.score;
      return (hScore === parseInt(details.home) && aScore === parseInt(details.away)) ? 'won' : 'lost';
    }

    case 'shutout':
      if (bet.selection === 'yes') {
        if (bothTeamsScored) return 'lost';
        if (isFinished) return (homeTeam.score === 0 || awayTeam.score === 0) ? 'won' : 'lost';
        return null;
      } else {
        if (isFinished) return bothTeamsScored ? 'won' : 'lost';
        if (bothTeamsScored) return 'won';
        return null;
      }

    case 'overtime':
      if (!isFinished) return null;
      // past the 3rd period = overtime (a shootout game went to OT too, even with no OT goal)
      const wentToOT = (gameDetails.currentPeriod || 0) > 3 || goals?.some(g => g.period > 3);
      if (bet.selection === 'yes') {
        return wentToOT ? 'won' : 'lost';
      } else {
        return !wentToOT ? 'won' : 'lost';
      }

    case 'fighting':
      if (!isFinished) return null;
      if (bet.selection === 'yes') {
        return gameDetails.fightOccurred ? 'won' : 'lost';
      } else {
        return !gameDetails.fightOccurred ? 'won' : 'lost';
      }

    case 'puckline':
      if (!isFinished) return null;
      {
        // lines are signed: home -1.5 = must win by 2+, away +1.5 = win, or lose by 1
        const lineRaw = parseFloat(details.line);
        const puckLine = Number.isNaN(lineRaw) ? (bet.selection === 'home' ? -1.5 : 1.5) : lineRaw;
        const scoreDiff = homeTeam.score - awayTeam.score;
        const margin = bet.selection === 'home' ? scoreDiff : -scoreDiff;
        return margin + puckLine > 0 ? 'won' : 'lost';
      }

    case 'total_shots':
      if (!isFinished) return null;
      const totalShots = (gameDetails.homeSog || 0) + (gameDetails.awaySog || 0);
      const shotsLine = parseFloat(details.line) || 60.5;
      if (bet.selection === 'over') {
        return totalShots > shotsLine ? 'won' : 'lost';
      } else {
        return totalShots < shotsLine ? 'won' : 'lost';
      }

    case 'player_shots': {
      if (!isFinished) return null;
      const pPlayerId = details.playerId;
      const pPlayerName = normalizeName(details.playerName);
      const pShotsLine = parseFloat(details.line) || 2.5;
      // Try matching by ID first, then exact name, then fuzzy name match
      let actualShots = gameDetails.playerShots?.[nhlIdOfPlayer(pPlayerId)] ?? gameDetails.playerShots?.[pPlayerId];
      if (actualShots === undefined && pPlayerName && gameDetails.playerShotsByName) {
        for (const [nhlName, shots] of Object.entries(gameDetails.playerShotsByName)) {
          if (samePlayerName(nhlName, details.playerName)) { actualShots = shots; break; }
        }
      }
      if (actualShots === undefined) return 'void';   // not in the boxscore: didn't play
      if (bet.selection === 'over') {
        return actualShots > pShotsLine ? 'won' : 'lost';
      } else {
        return actualShots < pShotsLine ? 'won' : 'lost';
      }
    }

    case 'penalty_mins':
      if (!isFinished) return null;
      if (gameDetails.totalPenaltyMins === undefined) return null;
      const pimLine = parseFloat(details.line) || 12.5;
      if (bet.selection === 'over') {
        return gameDetails.totalPenaltyMins > pimLine ? 'won' : 'lost';
      } else {
        return gameDetails.totalPenaltyMins < pimLine ? 'won' : 'lost';
      }

    case 'period_winner': {
      // Parse period from bet details: '1st' → 1, '2nd' → 2, '3rd' → 3
      const periodStr = details.period || '1st';
      const betPeriod = periodStr === '1st' ? 1 : periodStr === '2nd' ? 2 : periodStr === '3rd' ? 3 : 1;

      // Get current period from game details
      const currentPeriod = gameDetails.currentPeriod || 0;

      // Period is complete if: currentPeriod > betPeriod OR game is finished
      // Period 1 complete when: currentPeriod >= 2 OR isFinished
      // Period 2 complete when: currentPeriod >= 3 OR isFinished
      // Period 3 complete when: isFinished (or OT started, currentPeriod >= 4)
      const isPeriodComplete = betPeriod === 3
        ? (isFinished || currentPeriod >= 4)
        : (currentPeriod > betPeriod || isFinished);

      if (!isPeriodComplete) return null;

      // Count goals in the specific period for each team
      let homePeriodGoals = 0;
      let awayPeriodGoals = 0;
      if (goals && goals.length > 0) {
        goals.forEach(goal => {
          if (goal.period === betPeriod) {
            if (goal.teamAbbrev === homeTeam.abbrev) {
              homePeriodGoals++;
            } else if (goal.teamAbbrev === awayTeam.abbrev) {
              awayPeriodGoals++;
            }
          }
        });
      }

      // Determine winner
      if (homePeriodGoals > awayPeriodGoals) {
        return bet.selection === 'home' ? 'won' : 'lost';
      } else if (awayPeriodGoals > homePeriodGoals) {
        return bet.selection === 'away' ? 'won' : 'lost';
      } else {
        return bet.selection === 'draw' ? 'won' : 'lost';
      }
    }

    case 'mlb_batter_hr':
    case 'mlb_batter_rbi':
    case 'mlb_batter_sb':
    case 'mlb_batter_1b':
    case 'mlb_batter_2b':
    case 'mlb_batter_3b':
    case 'mlb_batter_ab': {
      if (!gameDetails.isFinished) return null;
      const pid = String(details.playerId);
      const stats = gameDetails.playerStats?.[pid];
      if (!stats) return 'void';   // didn't play
      const line = parseFloat(details.line) || 0.5;
      if (bet.bet_type === 'mlb_batter_hr') return stats.homeRuns > line ? 'won' : 'lost';
      if (bet.bet_type === 'mlb_batter_rbi') return stats.rbi > line ? 'won' : 'lost';
      if (bet.bet_type === 'mlb_batter_sb') return stats.stolenBases > line ? 'won' : 'lost';
      if (bet.bet_type === 'mlb_batter_1b') return stats.singles > line ? 'won' : 'lost';
      if (bet.bet_type === 'mlb_batter_2b') return stats.doubles > line ? 'won' : 'lost';
      if (bet.bet_type === 'mlb_batter_3b') return stats.triples > line ? 'won' : 'lost';
      return stats.atBats > line ? 'won' : 'lost';
    }

    case 'mlb_batter_cycle': {
      if (!gameDetails.isFinished) return null;
      const pid = String(details.playerId);
      const stats = gameDetails.playerStats?.[pid];
      if (!stats) return 'void';
      return (stats.singles >= 1 && stats.doubles >= 1 && stats.triples >= 1 && stats.homeRuns >= 1) ? 'won' : 'lost';
    }

    case 'mlb_pitcher_ks': {
      if (!gameDetails.isFinished) return null;
      const pid = String(details.playerId);
      const stats = gameDetails.playerStats?.[pid];
      if (!stats) return 'void';
      const ksLine = parseFloat(details.line) || 5.5;
      return bet.selection === 'over' ? (stats.strikeOuts > ksLine ? 'won' : 'lost') : (stats.strikeOuts < ksLine ? 'won' : 'lost');
    }

    case 'mlb_team_hr': {
      if (!gameDetails.isFinished) return null;
      const teamHR = bet.selection === 'home' ? (gameDetails.homeHR ?? 0) : (gameDetails.awayHR ?? 0);
      const hrLine = parseFloat(details.line) || 0.5;
      return teamHR > hrLine ? 'won' : 'lost';
    }

    case 'mlb_team_sb': {
      if (!gameDetails.isFinished) return null;
      const teamSB = bet.selection === 'home' ? (gameDetails.homeSB ?? 0) : (gameDetails.awaySB ?? 0);
      const sbLine = parseFloat(details.line) || 0.5;
      return teamSB > sbLine ? 'won' : 'lost';
    }

    case 'runline': {
      if (!gameDetails.isFinished) return null;
      const rlRaw = parseFloat(details.line);
      const rl = Number.isNaN(rlRaw) ? (bet.selection === 'home' ? -1.5 : 1.5) : rlRaw;   // signed, like the puck line
      const diff = (gameDetails.homeScore ?? 0) - (gameDetails.awayScore ?? 0);
      return ((bet.selection === 'home' ? diff : -diff) + rl) > 0 ? 'won' : 'lost';
    }

    case 'spread': {
      if (!gameDetails.isFinished) return null;
      const spreadLine = parseFloat(details.line) || 5.5;
      const sDiff = (gameDetails.homeScore ?? 0) - (gameDetails.awayScore ?? 0);
      // Home has negative line (e.g., -5.5), away has positive (e.g., +5.5)
      if (bet.selection === 'home') return (sDiff + spreadLine) > 0 ? 'won' : 'lost';
      return ((-sDiff) + spreadLine) > 0 ? 'won' : 'lost';
    }

    // ── NBA: quarters / halves, team totals, margin, overtime & extras ──
    case 'nba_quarter_winner': {
      const half = details.period === 'h1';
      const ready = half ? gameDetails.halfDone : gameDetails.q1Done;
      if (!ready && !gameDetails.isFinished) return null;
      const q = gameDetails.quarters;
      const n = half ? 2 : 1;
      if (!q || (q.home || []).length < n || (q.away || []).length < n) return gameDetails.isFinished ? 'void' : null;
      const h = q.home.slice(0, n).reduce((a, b) => a + b, 0), a = q.away.slice(0, n).reduce((x, y) => x + y, 0);
      if (h === a) return 'void';                       // level: stake back
      return bet.selection === (h > a ? 'home' : 'away') ? 'won' : 'lost';
    }
    case 'nba_half_total': {
      if (!gameDetails.halfDone && !gameDetails.isFinished) return null;
      const q = gameDetails.quarters;
      if (!q || (q.home || []).length < 2 || (q.away || []).length < 2) return gameDetails.isFinished ? 'void' : null;
      const tot = q.home[0] + q.home[1] + q.away[0] + q.away[1], l = parseFloat(details.line);
      return bet.selection === 'over' ? (tot > l ? 'won' : 'lost') : (tot < l ? 'won' : 'lost');
    }
    case 'nba_team_total': {
      if (!gameDetails.isFinished) return null;
      const v = details.team === 'home' ? gameDetails.homeScore : gameDetails.awayScore, l = parseFloat(details.line);
      return bet.selection === 'over' ? (v > l ? 'won' : 'lost') : (v < l ? 'won' : 'lost');
    }
    case 'nba_margin': {
      if (!gameDetails.isFinished) return null;
      const diff = (gameDetails.homeScore ?? 0) - (gameDetails.awayScore ?? 0);
      if (diff === 0) return 'void';
      if ((diff > 0 ? 'home' : 'away') !== bet.selection) return 'lost';
      const by = Math.abs(diff);
      const [lo, hi] = details.band === '16+' ? [16, Infinity] : String(details.band).split('-').map(Number);
      return by >= lo && by <= hi ? 'won' : 'lost';
    }
    case 'nba_overtime':
      if (!gameDetails.isFinished) return null;
      if (gameDetails.overtime == null) return 'void';
      return (gameDetails.overtime ? 'yes' : 'no') === bet.selection ? 'won' : 'lost';
    case 'nba_odd_even': {
      if (!gameDetails.isFinished) return null;
      const tot = (gameDetails.homeScore ?? 0) + (gameDetails.awayScore ?? 0);
      return (tot % 2 === 1 ? 'odd' : 'even') === bet.selection ? 'won' : 'lost';
    }
    case 'nba_both_110': {
      if (!gameDetails.isFinished) return null;
      const both = gameDetails.homeScore >= 110 && gameDetails.awayScore >= 110;
      return (both ? 'yes' : 'no') === bet.selection ? 'won' : 'lost';
    }
    // ── NBA players: combos, top scorer, first basket (a player who didn't play = stake back) ──
    case 'nba_player_pra':
    case 'nba_double_double':
    case 'nba_triple_double':
    case 'nba_top_scorer':
    case 'nba_first_basket': {
      const pid = String(details.playerId);
      const byName = gameDetails.playerStatsByName || {};
      const find = () => {
        let st = gameDetails.playerStats?.[pid];
        if (!st && details.playerName) {
          st = byName[details.playerName.toLowerCase()];
          if (!st) { const hit = Object.entries(byName).find(([nm]) => samePlayerName(nm, details.playerName)); st = hit ? hit[1] : null; }
        }
        return st || null;
      };
      if (bet.bet_type === 'nba_first_basket') {
        const fb = gameDetails.firstBasket;
        // it's him: won right away. Anything else waits for the end (did he play at all?)
        if (fb && (String(fb.playerId) === pid || (fb.name && details.playerName && samePlayerName(fb.name, details.playerName)))) return 'won';
        if (!gameDetails.isFinished) return null;
        if (!fb || !find()) return 'void';
        return 'lost';
      }
      if (!gameDetails.isFinished) return null;
      const st = find();
      if (!st) return 'void';
      if (bet.bet_type === 'nba_player_pra') {
        const v = (st.points || 0) + (st.rebounds || 0) + (st.assists || 0), l = parseFloat(details.line);
        return bet.selection === 'over' ? (v > l ? 'won' : 'lost') : (v < l ? 'won' : 'lost');
      }
      if (bet.bet_type === 'nba_top_scorer') {
        const all = Object.values(gameDetails.playerStats || {}).map((x) => x.points || 0);
        const best = Math.max(0, ...all);
        if ((st.points || 0) < best) return 'lost';
        return all.filter((v) => v === best).length > 1 ? 'void' : 'won';   // tied for the lead: stake back
      }
      const tens = [st.points, st.rebounds, st.assists, st.steals, st.blocks].filter((v) => (v || 0) >= 10).length;
      const hit = bet.bet_type === 'nba_double_double' ? tens >= 2 : tens >= 3;
      return (hit ? 'yes' : 'no') === (bet.selection === 'no' ? 'no' : 'yes') ? 'won' : 'lost';
    }
    case 'nba_player_pts':
    case 'nba_player_reb':
    case 'nba_player_ast':
    case 'nba_player_3pm':
    case 'nba_player_stl':
    case 'nba_player_blk': {
      if (!gameDetails.isFinished) return null;
      const nbaStatPid = String(details.playerId);
      // Try by ID first, then fall back to name match (ESPN IDs ≠ NBA personIds)
      let nbaStats = gameDetails.playerStats?.[nbaStatPid];
      if (!nbaStats && details.playerName && gameDetails.playerStatsByName) {
        nbaStats = gameDetails.playerStatsByName[details.playerName.toLowerCase()];
        if (!nbaStats) {
          const hit = Object.entries(gameDetails.playerStatsByName).find(([n]) => samePlayerName(n, details.playerName));
          nbaStats = hit ? hit[1] : null;
        }
      }
      if (!nbaStats) return 'void';
      const nbaLine = parseFloat(details.line) || 0.5;
      const statMap = {
        nba_player_pts: 'points', nba_player_reb: 'rebounds', nba_player_ast: 'assists',
        nba_player_3pm: 'threePointersMade', nba_player_stl: 'steals', nba_player_blk: 'blocks',
      };
      const statVal = nbaStats[statMap[bet.bet_type]] ?? 0;
      return bet.selection === 'over' ? (statVal > nbaLine ? 'won' : 'lost') : (statVal < nbaLine ? 'won' : 'lost');
    }

    default:
      return null;
  }
};

// which detail route grades a game id
const detailsRoute = (gameId) => {
  const id = String(gameId);
  if (id.startsWith('nfl-')) return `/api/nfl-game-details/${id}`;
  if (id.startsWith('f1-')) return `/api/f1-details/${id}`;
  if (id.startsWith('nba-')) return `/api/nba-game-details/${id}`;
  if (id.startsWith('mlb-')) return `/api/mlb-game-details/${id}`;
  return `/api/game-details/${id}`;
};

module.exports = { evaluateBet, normalizeName, samePlayerName, detailsRoute };
